import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { QuizEvaluationType } from '@edtech-ui/constants/quiz-evaluation.enum';
import { QuizQuestionType } from '@edtech-ui/constants/quiz-question-type.enum';
import { StudentIndividualQuizResultData } from '@edtech-ui/core/models/quiz-review-interface.ts/get-student-by-attempt-id';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-individual-student-result',
	standalone: true,
	imports: [MatProgressSpinnerModule, CommonModule, MatProgressSpinnerModule],
	templateUrl: './individual-student-result.component.html',
	styleUrl: './individual-student-result.component.scss'
})
export class IndividualStudentResultComponent implements OnInit, OnDestroy {

	subscription = new Subscription();
	attemptId: string = '';
	isLoading: boolean = false;
	studentResult: StudentIndividualQuizResultData | null = null;
	QuizQuestionType = QuizQuestionType;
	classroomId: number = 0;
	quizId: string = '';
	QuizEvaluationType = QuizEvaluationType;
	radius = 56;
	circumference = 2 * Math.PI * this.radius;
	selectedQuestionIndex: number | null = null;

	constructor(
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private router: Router
	) { }

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			this.attemptId = params.get('studentAttemptId') || '';
			this.getStudentIndividualDetails()
		});

		this.route.queryParamMap.subscribe(queryParams => {
			this.classroomId = Number(queryParams.get('classroomId')) || 0;
			this.quizId = queryParams.get('quizId') || '';
		});
	}

	getStudentIndividualDetails() {
		if (!this.attemptId) {
			this._toastr.error('Attempt ID is missing');
		}
		this.isLoading = true;
		this.subscription.add(
			this._quizService.getStudentIndividualDetails(this.attemptId).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.studentResult = response.Data || null;
					} else {
						this._toastr.error(response.Message);
					}
				}
				),
				finalize(()=>{
						this.isLoading = false;
				}),
				catchError(() => {
					this.isLoading = false;
					this._toastr.error('Something went wrong while fetching the students Details');
					return of(null);
				})
			).subscribe()
		);

	}

	convertToIST(utcDateString: string): string {
		if (!utcDateString) return '';
		const date = new Date(utcDateString + 'Z');
		return new Intl.DateTimeFormat('en-IN', {
	        timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short'}).format(date);
	}


	goToResultPage() {
		this.router.navigate(['/teacher-dashboard/students-results'], {
			queryParams: { classroomId: this.classroomId, quizId: this.quizId }
		});
	}

	getUserNameInitials(name: string): string {
		if (!name) return '';
		return name
			.split(' ').map(word => word[0]).join('').toUpperCase();
	}

	get scorePercentage(): number {
		return Number(
			String(this.studentResult?.overall_user_performance?.score_percentage ?? '0').replace('%', '').trim()
		);
	}

	get dashOffset() {
		const percent = this.scorePercentage / 100;
		return this.circumference * (1 - percent);
	}

	scrollToQuestion(index: number): void {
		const element = document.getElementById('question-' + index);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			this.selectedQuestionIndex = index;
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}